import { useEffect, useRef, useState } from "react";
import { Copy, Square, Terminal, Save, User } from "lucide-react";
import { toast } from "sonner";
import { streamAIAnswer } from "@/lib/api";
import { saveAnswer } from "@/lib/savedAnswers";
import { loadProjectContext } from "@/lib/projectContext";

export default function AITerminal({ question, context, questionId, sectionTitle, groupTitle, note, onClose }) {
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const bodyRef = useRef(null);

  const start = async () => {
    setText("");
    setError(null);
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const projectContext = loadProjectContext();
    try {
      await streamAIAnswer({
        question,
        context,
        note: note || null,
        projectContext: projectContext || null,
        signal: controller.signal,
        onChunk: (_chunk, full) => setText(full),
      });
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Erreur de génération");
      }
    } finally {
      setStreaming(false);
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Réponse copiée");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const saveToAntiseche = () => {
    if (!text || !questionId) return;
    saveAnswer(
      questionId,
      question,
      sectionTitle || "",
      groupTitle || "",
      text
    );
    toast.success("Ajoutée à l'antisèche", {
      description: "Disponible dans la page Antisèche IA",
    });
  };

  useEffect(() => {
    start();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [text]);

  return (
    <div
      data-testid="ai-terminal"
      className="bg-black border border-zinc-800 mt-4"
    >
      <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2 bg-zinc-950/70">
        <div className="flex items-center gap-2 text-zinc-400">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          </div>
          <Terminal className="w-3.5 h-3.5 ml-2" />
          <span className="text-[11px] uppercase tracking-widest">
            ai-answer · gpt-5.2
          </span>
        </div>
        <div className="flex items-center gap-2">
          {streaming ? (
            <button
              data-testid="ai-stop-btn"
              onClick={stop}
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
            >
              <Square className="w-3 h-3" /> Stop
            </button>
          ) : (
            <button
              data-testid="ai-regenerate-btn"
              onClick={start}
              className="text-[11px] uppercase tracking-widest text-sky-400 hover:text-sky-300 transition-colors"
            >
              Régénérer
            </button>
          )}
          {questionId && (
            <button
              data-testid="ai-save-btn"
              onClick={saveToAntiseche}
              disabled={!text || streaming}
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-30"
            >
              <Save className="w-3 h-3" /> Sauvegarder
            </button>
          )}
          <button
            data-testid="ai-copy-btn"
            onClick={copy}
            disabled={!text}
            className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-30"
          >
            <Copy className="w-3 h-3" /> Copier
          </button>
          {onClose && (
            <button
              data-testid="ai-close-btn"
              onClick={onClose}
              className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
      <div
        ref={bodyRef}
        className="px-5 py-4 font-mono text-sm leading-relaxed text-zinc-300 max-h-[480px] overflow-y-auto"
      >
        <div className="text-zinc-500 mb-3">
          <span className="text-sky-400">$</span> ais-prep --question "
          <span className="text-zinc-300">{question}</span>"
        </div>
        {(note || loadProjectContext()) && (
          <div
            data-testid="ai-personalization-badge"
            className="text-emerald-400 text-[11px] uppercase tracking-widest mb-3 flex items-center gap-1"
          >
            <User className="w-3 h-3" />
            {note && loadProjectContext()
              ? "réponse personnalisée · projet + note"
              : note
              ? "réponse personnalisée · note"
              : "réponse personnalisée · projet"}
          </div>
        )}
        {error ? (
          <div className="text-red-400">[erreur] {error}</div>
        ) : (
          <pre
            data-testid="ai-output"
            className="whitespace-pre-wrap font-mono text-sm text-zinc-200"
          >
            {text}
            {streaming && <span className="terminal-cursor" />}
          </pre>
        )}
        {!streaming && !error && text && (
          <div className="text-emerald-400 mt-3 text-xs">
            ✓ génération terminée — {text.length} caractères
          </div>
        )}
      </div>
    </div>
  );
}
